import { BaseService } from "./BaseService";
import {
  CommunicationIntegration,
  CommunicationIntegrationKind,
  IntegrationHealthProbeResult,
  ToggleIntegrationRequest,
  UpsertEmailIntegrationRequest,
  UpsertMetaWhatsappIntegrationRequest,
  UpsertTwilioIntegrationRequest,
} from "@/types/communication-integration";

class CommunicationIntegrationsService extends BaseService {
  private basePath(hotelId: string): string {
    return `/hotels/${hotelId}/communication-integrations`;
  }

  /**
   * The configured API client base URL (already includes the backend global
   * prefix, e.g. ".../api"). Used to derive the public WhatsApp webhook
   * callback URL the admin pastes into the Meta App dashboard — never hardcode
   * "/api" so it survives env/prefix differences.
   */
  getApiBaseUrl(): string {
    return this.baseUrl;
  }

  list(hotelId: string): Promise<CommunicationIntegration[]> {
    return this.get<CommunicationIntegration[]>(this.basePath(hotelId));
  }

  getByKind(
    hotelId: string,
    kind: CommunicationIntegrationKind,
  ): Promise<CommunicationIntegration> {
    return this.get<CommunicationIntegration>(
      `${this.basePath(hotelId)}/${kind}`,
    );
  }

  upsertEmail(
    hotelId: string,
    payload: UpsertEmailIntegrationRequest,
  ): Promise<CommunicationIntegration> {
    return this.put<CommunicationIntegration>(
      `${this.basePath(hotelId)}/EMAIL`,
      payload,
    );
  }

  upsertTwilio(
    hotelId: string,
    payload: UpsertTwilioIntegrationRequest,
  ): Promise<CommunicationIntegration> {
    return this.put<CommunicationIntegration>(
      `${this.basePath(hotelId)}/TWILIO`,
      payload,
    );
  }

  upsertWhatsapp(
    hotelId: string,
    payload: UpsertMetaWhatsappIntegrationRequest,
  ): Promise<CommunicationIntegration> {
    return this.put<CommunicationIntegration>(
      `${this.basePath(hotelId)}/META_WHATSAPP`,
      payload,
    );
  }

  setEnabled(
    hotelId: string,
    kind: CommunicationIntegrationKind,
    enabled: boolean,
  ): Promise<CommunicationIntegration> {
    const body: ToggleIntegrationRequest = { enabled };
    return this.patch<CommunicationIntegration>(
      `${this.basePath(hotelId)}/${kind}/enabled`,
      body,
    );
  }

  remove(hotelId: string, kind: CommunicationIntegrationKind): Promise<void> {
    return this.delete<void>(`${this.basePath(hotelId)}/${kind}`);
  }

  test(
    hotelId: string,
    kind: CommunicationIntegrationKind,
  ): Promise<IntegrationHealthProbeResult> {
    return this.post<IntegrationHealthProbeResult>(
      `${this.basePath(hotelId)}/${kind}/test`,
    );
  }
}

export const communicationIntegrationsService =
  new CommunicationIntegrationsService();
